import hasSessionStorage from "@mgdx/shared/src/utils/hasSessionStorage";
import { tzDate } from "@mgdx-libs/dayjs";
import { errorHandlerReport } from "@mgdx-libs/error-handler";

// 処理中フラグが解除されずに残った場合でも、一定時間で認証状態監視が再開されるようにする
const SESSION_EXPIRE_MINUTE = 5;

// 保存時刻が無い・不正な場合も期限切れとして扱い、フラグが無期限に残らないようにする
const isExpired = (saveTimeString: string | null): boolean => {
  if (!saveTimeString) return true;
  const saveTime = tzDate(saveTimeString);
  if (!saveTime.isValid()) return true;
  // diff の単位指定は端数を切り捨てるため、5 分ちょうどで失効するよう小数で比較する
  // (切り捨てのままだと 5 分 00 秒〜5 分 59 秒も処理中と判定され、実質 6 分間フラグが残る)
  return tzDate().diff(saveTime, "minute", true) >= SESSION_EXPIRE_MINUTE;
};

export type InProcessOfSignupStorage = {
  set: (inProcess: boolean) => void;
  clear: () => void;
  get: () => boolean;
};

type CreateInProcessOfSignupStorageArgs = {
  storageKey: string;
  timeStorageKey: string;
};

/**
 * 外部認証(OIDC / LIFF)の処理中であることを sessionStorage に保持するストレージを作成する。
 * 処理中は AuthProvider の認証状態監視をスキップさせるために使い、保存から一定時間経過すると自動的に解除される。
 */
export const createInProcessOfSignupStorage = ({
  storageKey,
  timeStorageKey,
}: CreateInProcessOfSignupStorageArgs): InProcessOfSignupStorage => {
  // sessionStorage を操作できない環境でも、同一タブ内では認証状態監視をスキップできるようにするフォールバック。
  // フラグが立たないまま認証を続けると、未登録ユーザーの sign-in を AuthProvider の監視も処理してしまい、
  // 患者取得の 404 でサインアウトされて新規登録フローへセッションを引き継げなくなる
  let inMemoryState: { inProcess: boolean; saveTime: string } | undefined;

  const getFromMemory = (): boolean => {
    if (!inMemoryState?.inProcess) return false;
    if (isExpired(inMemoryState.saveTime)) {
      inMemoryState = undefined;
      return false;
    }
    return true;
  };

  // 認証処理を止めないための補助フラグのため、容量上限などで sessionStorage を操作できない場合も例外を外へ出さない
  // ストレージがポリシーや opaque origin で禁止された環境では window.sessionStorage の取得自体が
  // SecurityError を投げるため、可用性チェックも try の内側で評価する
  const set = (inProcess: boolean): void => {
    const saveTime = tzDate().toISOString();
    inMemoryState = { inProcess, saveTime };
    try {
      if (!hasSessionStorage()) return;
      // 時刻だけ保存できてフラグを保存できない場合に in-memory へフォールバックできるよう、時刻を先に書く
      // (逆順だと保存時刻の無いフラグが残り、期限切れ扱いで in-memory ごと解除されてしまう)
      sessionStorage.setItem(timeStorageKey, saveTime);
      sessionStorage.setItem(storageKey, inProcess ? "yes" : "no");
    } catch (error) {
      // 保存できない場合は同一タブ内の in-memory フラグだけで処理中を表す
      errorHandlerReport(error instanceof Error ? error : String(error));
    }
  };

  const clear = (): void => {
    inMemoryState = undefined;
    try {
      if (!hasSessionStorage()) return;
      sessionStorage.removeItem(storageKey);
      sessionStorage.removeItem(timeStorageKey);
    } catch (error) {
      errorHandlerReport(error instanceof Error ? error : String(error));
    }
  };

  const get = (): boolean => {
    try {
      if (hasSessionStorage()) {
        const storedValue = sessionStorage.getItem(storageKey);
        if (storedValue !== null) {
          if (storedValue !== "yes") return false;
          if (isExpired(sessionStorage.getItem(timeStorageKey))) {
            clear();
            return false;
          }
          return true;
        }
      }
    } catch (error) {
      // 読み取れない場合は同一タブ内の in-memory フラグで判定する
      errorHandlerReport(error instanceof Error ? error : String(error));
    }
    return getFromMemory();
  };

  return { set, clear, get };
};
